import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { FreelancerAsyncPipeModule } from '@freelancer/pipes';
import { isDefined } from '@freelancer/utils';
import type { Observable } from 'rxjs';
import { map, scan, shareReplay, startWith } from 'rxjs/operators';
import { ValidationErrorComponent } from '../validation-error';
import { TimepickerInterval } from './timepicker.types';

@Component({
  standalone: true,
  selector: 'fl-timepicker',
  imports: [
    FreelancerAsyncPipeModule,
    MatTimepickerModule,
    ReactiveFormsModule,
    ValidationErrorComponent,
  ],
  template: `
    @let borderStyle = borderStyle$ | flAsync;

    <div
      class="Container"
      [attr.data-disabled]="isDisabled || undefined"
      [attr.data-invalid]="borderStyle === 'invalid' || undefined"
      [attr.data-valid]="borderStyle === 'valid' || undefined"
    >
      <input
        class="NativeElement"
        [attr.aria-label]="label"
        [attr.disabled]="isDisabled || undefined"
        [formControl]="control"
        [matTimepicker]="picker"
        [matTimepickerMin]="min"
        [matTimepickerMax]="max"
        [placeholder]="placeholder"
      />
      <mat-timepicker-toggle
        class="Toggle"
        [disabled]="isDisabled"
        [for]="picker"
      />
      <mat-timepicker
        #picker
        [interval]="interval"
      />
    </div>
    @if (displayValidation) {
      <fl-validation-error
        [control]="control"
        [showIcon]="false"
      ></fl-validation-error>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./timepicker.component.scss'],
})
export class TimepickerComponent implements OnChanges {
  /** Expands to fill the parent width instead of fitting the time value. */
  @Input() @HostBinding('class.MaxWidth') maxWidth = false;

  /** FormControl that holds the selected time as a Date object. */
  @Input({ required: true }) control: FormControl<Date | undefined>;

  /** Time step between each option in the dropdown. */
  @Input() interval: TimepickerInterval = TimepickerInterval.THIRTY_MINUTES;

  /** Earliest selectable time. Only the time portion of the Date is used. */
  @Input() min: Date | undefined;

  /** Latest selectable time. Only the time portion of the Date is used. */
  @Input() max: Date | undefined;

  /** Placeholder text shown when no time is selected. */
  @Input() placeholder = '—— : ——  ——';

  /** Accessible label for the time input, used by screen readers. */
  @Input() label?: string;

  isDisabled = false;

  /** Disable the time input and toggle. */
  @Input() set disabled(value: boolean) {
    this.isDisabled = value;
    if (value) {
      this.control?.disable();
    } else {
      this.control?.enable();
    }
  }

  /** Display the validation error message. */
  @Input() displayValidation = true;

  borderStyle$: Observable<'neutral' | 'invalid' | 'valid'>;

  ngOnChanges(changes: SimpleChanges): void {
    if ('control' in changes) {
      this.setupBorderStyle();
    }
  }

  /**
   * Styles the component border by deriving the validation state from the control:
   * - `invalid` while input is invalid and has been interacted with.
   * - `valid` once invalid input is fixed after a non-empty input error.
   * - `neutral` otherwise (never errored controls get no validation styling).
   *
   * The styling behaviour should closely match `fl-input`
   * @see InputComponent.errorSubscription
   */
  private setupBorderStyle(): void {
    this.borderStyle$ = this.control.events.pipe(
      startWith(undefined),
      // Latch to true once a non-empty input has ever been invalid. The value
      // check excludes required but empty controls, so tabbing through an
      // empty field then filling it in doesn't flash the 'valid' state.
      scan(hadError => {
        const hasNonEmptyError =
          this.control.invalid &&
          (this.control.touched || this.control.dirty) &&
          isDefined(this.control.value);
        return hadError || hasNonEmptyError;
      }, false),
      map(hadError => {
        if (
          this.control.invalid &&
          (this.control.touched || this.control.dirty)
        ) {
          return 'invalid';
        }
        if (hadError && this.control.valid) {
          return 'valid';
        }
        return 'neutral';
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}
